import { TuttaItalia } from '@sbt-web/networking';
import { Category } from '@sbt-web/category-utils';
import {
  AdTypes,
  type GeoEntry,
  HTTPStatusCode,
  type SerializableFilter,
} from '@sbt-web/network/types';
import { mapActualQueryStringToSEO } from '@client/components/SEO/helpers/automotive';
import {
  SearchState,
  updateFilter,
  updateGeo,
  updateRadiusSearch,
} from '@reducers/search';
import type { ListingQuery } from '@shared/routes';
import type { Action, Store } from '@reduxjs/toolkit';
import { slugToAdTypeMap } from './values';
import { getErrorMessage } from '@tools/errorHelpers';
import { geoSearch } from '@sbt-web/network/client';

const geogosCache: { [k: string]: GeoEntry } = {
  [TuttaItalia.friendlyName]: { region: TuttaItalia },
};

export class ListingHelper {
  public constructor(
    private _store: Store,
    private _query: ListingQuery
  ) {}

  public checkThenDispatch(condition: boolean, action: Action): void {
    if (condition) {
      this._store.dispatch(action);
    }
  }

  public getAdTypeForCategory(category: Category): AdTypes | undefined {
    const queryAdType = slugToAdTypeMap[this._query.type];
    return category.adTypes.find((t) => t === queryAdType);
  }

  public checkDynamicFilterThenDispatch(
    filter: SerializableFilter,
    search: SearchState
  ): void {
    const searchFilterValue = search.filters[filter.queryString];
    let queryFilterValue =
      this._query[mapActualQueryStringToSEO(filter.queryString)] ||
      this._query[filter.queryString];
    // Remove false boolean filters in advance
    if (queryFilterValue === 'false') {
      queryFilterValue = undefined;
    }
    if (queryFilterValue && searchFilterValue !== queryFilterValue) {
      this._store.dispatch(
        updateFilter({
          key: filter.queryString,
          value: queryFilterValue,
          uri: filter.uri,
        })
      );
    } else if (searchFilterValue && !queryFilterValue) {
      this._store.dispatch(
        updateFilter({ key: filter.queryString, value: '', uri: filter.uri })
      );
    }
  }

  public async checkGeoThenDispatch(
    search: SearchState
  ): Promise<[HTTPStatusCode, string | undefined]> {
    // We short-circuit the regional logic for radius searches
    // We might need different logic to stop wasted searches when cancelling
    // Since the internal state doesn't match the URL in that case.
    if (this._query.region === 'locali') {
      if (
        'rad' in this._query &&
        'lat' in this._query &&
        'lon' in this._query
      ) {
        if (__BROWSER__) {
          // We only need to dispatch on the server
          // The client handles the state updates
          return [HTTPStatusCode.OK, undefined];
        }

        try {
          this._store.dispatch(
            updateRadiusSearch({
              radiusSearch: {
                center: {
                  lat: Number.parseFloat(this._query.lat as string),
                  lng: Number.parseFloat(this._query.lon as string),
                },
                radiusMeters: Number.parseFloat(this._query.rad as string),
                addressToTrack: search.radiusSearch?.addressToTrack,
                friendlyName:
                  search.radiusSearch?.friendlyName ??
                  'Il punto che hai selezionato',
              },
              isFinalSearch: true,
            })
          );
          return [HTTPStatusCode.OK, undefined];
        } catch (error) {
          console.error(
            'There was an error in the dispatch to update the radius search:',
            getErrorMessage(error)
          );
          return [HTTPStatusCode.InternalServerError, undefined];
        }
      } else {
        console.error(
          'The query does not have all of the rad, lat and lon parameters.'
        );
        return [HTTPStatusCode.NotFound, undefined];
      }
    }

    let friendly = this._query.region; // This should be always set, otherwise listing regexp would not match
    let cacheKey = friendly; // And we start by building the cache key based on region
    let hasTown = false; // We also need to know if we need to grab the entry with town or not

    // First we exclude URLs with the "near" param and either "city" or "town"
    // Because asking for regions near a city or town is not valid
    if (!!this._query.near && (this._query.city || this._query.town)) {
      return [HTTPStatusCode.NotFound, undefined];
    }

    // If the city is set in the query
    if (this._query.city) {
      friendly = this._query.city; // We update the friendly term
      cacheKey = `${cacheKey}-${friendly}`; // And add it to the cache key
    }

    // Same for town
    if (this._query.town) {
      friendly = this._query.town;
      cacheKey = `${cacheKey}-${friendly}`;
      hasTown = true; // Except for the fact that we update the corresponding flag
    }

    // So if our geo state is different from what's in the URL
    if (this._isGeoStateChanged(search)) {
      if (geogosCache[cacheKey]) {
        // And the value is present in cache
        this._store.dispatch(
          updateGeo(
            this.addZoneIfNeeded(hasTown, geogosCache[cacheKey]),
            !!this._query.near
          )
        ); // We can immediately dispatch
      } else {
        // If not, we need to fetch the corresponding Geo state
        try {
          // So we search for the geo entry that corresponds to our friendly name
          const {
            body: { entries },
          } = await geoSearch(
            process.env.GEOGOS_BASE_URL ??
              process.env.NEXT_PUBLIC_HADES_BASE_URL,
            '',
            {
              friendly_name: friendly,
              lim: 10,
            }
          );
          const entry = entries?.find(
            (entry) => (hasTown ? !!entry.town : !entry.town) // And we pick the one we want based on our hasTown flag
          );

          // So if our entry matches URL geo components
          if (entry && this._isGeoMatchingQuery(entry)) {
            this._store.dispatch(
              updateGeo(
                this.addZoneIfNeeded(hasTown, entry),
                !!this._query.near
              )
            ); // We can dispatch

            // TODO: investigate
            // eslint-disable-next-line require-atomic-updates
            geogosCache[cacheKey] = entry; // And store the new value to the cache
          } else {
            // Or set a "not found" status code
            return [HTTPStatusCode.NotFound, 'Geo not found'];
          }
        } catch (error) {
          const statusMsg = `Unable to get/parse Geo info:${getErrorMessage(error)}`;

          return [HTTPStatusCode.InternalServerError, statusMsg];
        }
      }
    } else if (!geogosCache[cacheKey]) {
      // There's one last case, when the update comes from the front-end and we don't have the value in cache
      geogosCache[cacheKey] = search.geo;
    }
    return [HTTPStatusCode.OK, undefined];
  }

  private addZoneIfNeeded(hasTown: boolean, entry: GeoEntry): GeoEntry {
    const realEntry = {
      ...entry,
    };
    if (hasTown && entry.town && entry.town.hasZone && this._query.z) {
      let zoneId = this._query.z;
      if (!zoneId.includes('-')) {
        // We need to manually compose the Zone ID by prepending the Town ID
        // in order to support legacy URLs which were not containing it (e.g. saved searches)
        zoneId = `${entry.town.id}-${zoneId}`;
      }

      realEntry.zone = {
        uri: '/geo/zone',
        id: zoneId,
        value: '',
        label: '',
        level: 3,
        regionId: entry.town.regionId,
        cityId: entry.town.cityId,
        townId: entry.town.id,
      };
    }
    return realEntry;
  }

  private _isGeoStateChanged(search: SearchState): boolean {
    const { geo, searchNearRegions } = search;
    return (
      this._isZoneChanged(geo) ||
      this._isTownChanged(geo) ||
      this._isCityChanged(geo) ||
      this._query.region !== geo.region.friendlyName ||
      searchNearRegions !== !!this._query.near
    );
  }

  private _isZoneChanged(geo: GeoEntry): boolean {
    return geo.zone ? this._query.z !== geo.zone.id : !!this._query.z;
  }

  private _isTownChanged(geo: GeoEntry): boolean {
    return geo.town
      ? this._query.town !== geo.town.friendlyName
      : !!this._query.town;
  }

  private _isCityChanged(geo: GeoEntry): boolean {
    return geo.city
      ? this._query.city !== geo.city.friendlyName
      : !!this._query.city;
  }

  private _isGeoMatchingQuery(geo: GeoEntry): boolean {
    return (
      geo.region.friendlyName === this._query.region &&
      (geo.city ? geo.city.friendlyName === this._query.city : true) &&
      (geo.town ? geo.town.friendlyName === this._query.town : true) &&
      (this._query.near ? !geo.city && !geo.town : true)
    );
  }
}
